/**
 * Regular expression patterns that match Giphy media URLs.
 * Covers the most common Giphy URL formats:
 * - https://media.giphy.com/media/.../giphy.gif
 * - https://media0-4.giphy.com/media/...
 * - https://i.giphy.com/...
 * - https://giphy.com/gifs/...
 */
const GIPHY_URL_PATTERN =
    /^https?:\/\/(?:media\d?\.giphy\.com\/media\/[^\s]+|i\.giphy\.com\/[^\s]+\.(?:gif|webp)|giphy\.com\/gifs\/[^\s]+)$/i;

/**
 * Check if a string is a single Giphy URL (no other text).
 * Used to determine whether to render a message as an embedded GIF.
 * @param text The message content to check.
 * @returns The text is a single Giphy URL.
 */
export function isSingleGiphyUrl(text: string): boolean {
    const trimmed = text.trim();
    return GIPHY_URL_PATTERN.test(trimmed);
}

/**
 * Extract the direct GIF image URL from any Giphy URL format.
 * Normalises various Giphy URL formats to a direct media URL.
 * @param url The Giphy URL.
 * @returns The direct image URL, or the original URL if already a direct link.
 */
export function getGiphyImageUrl(url: string): string {
    const trimmed = url.trim();

    // Already a direct media URL (e.g. i.giphy.com or media*.giphy.com)
    if (/^https?:\/\/(?:media\d?\.giphy\.com|i\.giphy\.com)\//i.test(trimmed)) {
        return trimmed;
    }

    // Extract ID from giphy.com/gifs/ URL and build direct media URL
    const giphyGifsMatch = trimmed.match(
        /giphy\.com\/gifs\/(?:.*-)?([a-zA-Z0-9]+)$/,
    );
    if (giphyGifsMatch?.[1]) {
        return `https://media.giphy.com/media/${giphyGifsMatch[1]}/giphy.gif`;
    }

    return trimmed;
}
